<template>
  <a-button
    type="outline"
    :size="size"
    :class="[
      'round-button',
      `round-button--${type}`,
      { 'is-active': active },
      loading ? `round-button--loading-${type}` : '',
      { 'round-button--loading': loading },
    ]"
    :disabled="disabled"
    :loading="loading"
    @click="handleClick"
  >
    <template v-if="$slots.icon || $slots.prefix" #icon>
      <slot name="prefix">
        <slot name="icon" />
      </slot>
    </template>
    <span class="round-button__content">
      <slot />
      <span v-if="$slots.suffix" class="round-button__suffix">
        <slot name="suffix" />
      </span>
    </span>
  </a-button>
</template>

<script setup>
/**
 * @description 圆形镂空按钮组件
 * @param {string} type - 按钮类型：'default' | 'primary'，默认为 'default'
 * @param {string} size - 按钮尺寸：'mini' | 'small' | 'medium' | 'large'，默认为 'large'
 * @param {boolean} active - 是否激活状态，默认为 false
 * @param {boolean} disabled - 是否禁用，默认为 false
 * @param {boolean} loading - 是否加载中，默认为 false
 * @emits click - 点击事件
 * @slot prefix - 前置图标插槽
 * @slot suffix - 后置图标插槽
 * @slot icon - 兼容旧版图标插槽
 */

const props = defineProps({
  type: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'primary'].includes(value),
  },
  size: {
    type: String,
    default: 'large',
    validator: (value) => ['mini', 'small', 'medium', 'large'].includes(value),
  },
  active: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['click'])

/**
 * @description 处理点击事件
 */
const handleClick = (e) => {
  if (!props.disabled && !props.loading) {
    emit('click', e)
  }
}
</script>

<style lang="scss" scoped>
.round-button {
  border-radius: $radius-6;
  font-size: 14px;
  line-height: 22px;
  padding: 0 16px;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 400;

  :deep(.arco-btn-content) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  :deep(.arco-btn-icon) {
    margin-right: 6px;
    font-size: 16px;
  }

  :deep(.arco-icon) {
    font-size: 16px;
  }

  &__content {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  &__suffix {
    display: inline-flex;
    align-items: center;
  }

  &--loading {
    cursor: default;
    opacity: 0.7 !important;
    pointer-events: none;
  }

  &--loading-default {
    border-color: $border-light-color !important;
    color: $text-title-color !important;
    background-color: $bg-white !important;
  }

  &--loading-primary {
    border-color: $primary-color !important;
    color: $primary-color !important;
    background-color: $bg-white !important;
  }

  &--loading-primary.is-active {
    background-color: $primary-color !important;
    color: $bg-white !important;
  }

  &:disabled {
    border-color: #b0b3b7 !important;
    color: $text-subtitle-color !important;
    background-color: $bg-white !important;
    cursor: not-allowed;

    :deep(.arco-btn) {
      border-color: inherit;
      color: inherit;
      background-color: inherit;
    }
  }

  &--default {
    border-color: $border-light-color;
    color: $text-title-color;
    background-color: $bg-white;

    &:hover:not(:disabled):not(.round-button--loading) {
      border-color: $primary-color;
      color: $primary-color;
      background-color: $bg-white;
    }

    &.is-active {
      border-color: $primary-color;
      color: $primary-color;
    }

    &:disabled:not(.round-button--loading) {
      :deep(.arco-btn) {
        border-color: $border-dashed-color;
        color: $text-subtitle-color;
        background-color: $bg-white;
        cursor: not-allowed;
      }
    }
  }

  &--primary {
    border-color: $primary-color;
    color: $primary-color;
    background-color: $bg-white;
    &:hover:not(:disabled):not(.round-button--loading) {
      border-color: $primary-color;
      color: $bg-white;
      background-color: $primary-color;
    }

    &.is-active {
      border-color: $primary-color;
      color: $bg-white;
      background-color: $primary-color;
    }

    &:disabled:not(.round-button--loading) {
      border-color: $border-dashed-color;
      color: $text-subtitle-color;
      background-color: $bg-white;
      cursor: not-allowed;
    }
  }

  &.arco-btn-size-small {
    height: 32px;
    padding: 0 16px;
  }

  &.arco-btn-size-medium,
  &.arco-btn-size-large {
    height: 40px;
    padding: 0 16px;
  }
}
</style>
